import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link2Off, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const dismissedKey = (userId: number) => `jira-link-banner-dismissed:${userId}`;

// Remembered for the browser session only: the notice comes back next time until the login is linked.
const wasDismissed = (userId: number) => {
  try {
    return sessionStorage.getItem(dismissedKey(userId)) === '1';
  } catch {
    return false;
  }
};

const remember = (userId: number) => {
  try {
    sessionStorage.setItem(dismissedKey(userId), '1');
  } catch {
    // Private windows can refuse storage; the notice then just stays until the next load.
  }
};

// A login is tied to its Jira person by the stored link. Without it the pages that show "your own"
// work have nothing to show, so say so once, near the top, and say who can fix it.
export default function JiraLinkBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [, redraw] = useState(0);

  // Only an explicit null from the server means "not linked"; undefined means it has not said yet.
  if (!user || user.jiraAccountId !== null || wasDismissed(user.id)) return null;
  const isAdmin = user.role === 'admin';

  const dismiss = () => {
    remember(user.id);
    redraw((n) => n + 1);
  };

  return (
    <div role="status" className="border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-900">
      <div className="mx-auto flex max-w-7xl items-start gap-3">
        <Link2Off className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        {/* On a phone the button sits under the text; from sm up it sits beside it. */}
        <div className="min-w-0 flex-1 sm:flex sm:items-center sm:gap-3">
          <p className="min-w-0 sm:flex-1">
            Your login isn't linked to a Jira account yet, so the pages that show your own work stay empty.{' '}
            {isAdmin ? 'Link it in Settings → Create users.' : 'Ask an admin to link it in Settings → Create users.'}
          </p>
          {isAdmin && (
            <button
              type="button"
              onClick={() => navigate('/settings?tab=create-users')}
              className="mt-2 shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-amber-900 ring-1 ring-inset ring-amber-300 transition-[transform,background-color] duration-150 hover:bg-amber-100 active:scale-[0.98] motion-reduce:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 sm:mt-0"
            >
              Open Create users
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss this notice"
          className="-mr-1 shrink-0 rounded-lg p-1.5 text-amber-700 transition-[transform,background-color] duration-150 hover:bg-amber-100 active:scale-95 motion-reduce:transform-none focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
